import { z } from 'zod';
/**
 * Validates process.env at application startup.
 *
 * Required fields have defaults so the app can boot with minimal config.
 * Optional Azure / Anthropic / SF fields are validated only when present.
 * The runtime caller is responsible for asserting that at least one auth group
 * is present (e.g. AZURE_STORAGE_CONNECTION_STRING or the tenant/client/secret
 * triple) — the schema itself does not enforce cross-field requirements so that
 * tests can boot with a minimal env.
 *
 * Empty-string values (common in .env template files) are treated as absent
 * for all optional fields.
 */
/** Converts empty strings to undefined so .env placeholder lines are ignored. */
function emptyToUndef(val) {
    return val === '' ? undefined : val;
}
export const EnvSchema = z.object({
    // ── Runtime ────────────────────────────────────────────────────────────────
    NODE_ENV: z
        .enum(['development', 'test', 'production'])
        .default('development'),
    PORT: z.coerce.number().int().min(1).max(65535).default(3001),
    // ── Azure Storage ──────────────────────────────────────────────────────────
    AZURE_STORAGE_CONNECTION_STRING: z.preprocess(emptyToUndef, z.string().optional()),
    AZURE_TENANT_ID: z.preprocess(emptyToUndef, z.string().optional()),
    AZURE_CLIENT_ID: z.preprocess(emptyToUndef, z.string().optional()),
    AZURE_CLIENT_SECRET: z.preprocess(emptyToUndef, z.string().optional()),
    AZURE_KEY_VAULT_URL: z.preprocess(emptyToUndef, z.string().url().optional()),
    // ── Authentication (Entra ID) ───────────────────────────────────────────────
    // Note AZURE_TENANT_ID above is SHARED with the storage credential — same tenant, one value.
    // AZURE_CLIENT_ID / _SECRET above are a DIFFERENT credential (the storage service principal); the
    // API's audience is AZURE_API_CLIENT_ID below. Conflating them is an easy and costly mistake, so
    // they are validated as GUIDs: a pasted display name or an `api://…` URI is caught here rather
    // than degrading into a blanket "auth not configured" that reads like a code bug.
    AZURE_API_CLIENT_ID: z.preprocess(emptyToUndef, z.string().uuid("AZURE_API_CLIENT_ID must be the app registration's client id (a GUID) — not the Application ID URI or display name").optional()),
    /** Comma-separated UPNs treated as admin with no role-table row (break-glass for a fresh deploy). */
    // Provider-agnostic OIDC. AZURE_TENANT_ID / AZURE_API_CLIENT_ID remain as the Entra aliases, so
    // nothing has to change for the current install; these are what a customer on Okta, Auth0, Cognito
    // or Google sets instead. See auth/entraToken.ts for the precedence.
    OIDC_ISSUER: z.preprocess(emptyToUndef, z.string().url().optional()),
    OIDC_AUDIENCE: z.preprocess(emptyToUndef, z.string().optional()),
    OIDC_JWKS_URI: z.preprocess(emptyToUndef, z.string().url().optional()),
    STRIDE_BOOTSTRAP_ADMINS: z.preprocess(emptyToUndef, z.string().optional()),
    /** LOCAL ONLY. Ignored on deployed instances (see authMiddleware.looksDeployed). */
    STRIDE_AUTH_DEV_BYPASS: z.preprocess(emptyToUndef, z.enum(['true', 'false']).optional()),
    STRIDE_DEV_USER: z.preprocess(emptyToUndef, z.string().optional()),
    // ── AI ─────────────────────────────────────────────────────────────────────
    ANTHROPIC_API_KEY: z.preprocess(emptyToUndef, z.string().optional()),
    // ── Salesforce ─────────────────────────────────────────────────────────────
    SF_SOURCE_ORG_ALIAS: z.preprocess(emptyToUndef, z.string().optional()),
    SF_TARGET_ORG_ALIAS: z.preprocess(emptyToUndef, z.string().optional()),
    // ── Azure DevOps ───────────────────────────────────────────────────────────
    // Required at runtime by the git proxy routes, validated per-request so the
    // app can still boot (and tests can run) without them set.
    AZURE_DEVOPS_TOKEN: z.preprocess(emptyToUndef, z.string().optional()),
    AZURE_DEVOPS_ORG: z.preprocess(emptyToUndef, z.string().optional()),
    // ── F-conn connections ─────────────────────────────────────────────────────
    // CONNECTION_ENC_KEY: 32 random bytes encoded as standard base64 (44 chars).
    // Generate with: node -e "console.log(require('crypto').randomBytes(32).toString('base64'))"
    // Required at runtime when any connection is created/resolved; the app can
    // still boot without it (tests, minimal dev startup).
    CONNECTION_ENC_KEY: z.preprocess(emptyToUndef, z.string()
        .superRefine((v, ctx) => {
        const decoded = Buffer.from(v, 'base64');
        if (decoded.length !== 32) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `CONNECTION_ENC_KEY must decode to 32 bytes; got ${decoded.length}`,
            });
        }
    })
        .optional()),
    // Salesforce Connected App (OAuth flow only — paste method works without these).
    SF_CONNECTED_APP_CLIENT_ID: z.preprocess(emptyToUndef, z.string().optional()),
    SF_CONNECTED_APP_SECRET: z.preprocess(emptyToUndef, z.string().optional()),
    SF_LOGIN_URL: z.preprocess(emptyToUndef, z.string().url().optional()),
    // GitHub OAuth App (OAuth flow only).
    GITHUB_OAUTH_CLIENT_ID: z.preprocess(emptyToUndef, z.string().optional()),
    GITHUB_OAUTH_SECRET: z.preprocess(emptyToUndef, z.string().optional()),
    // Public base URL for OAuth callbacks, e.g. https://stride.example.com
    APP_URL: z.preprocess(emptyToUndef, z.string().url().optional()),
});
/** Parse and validate process.env, returning the typed result. Throws on failure. */
export function parseEnv(env) {
    return EnvSchema.parse(env);
}
//# sourceMappingURL=env.js.map